import React, { ReactNode, forwardRef } from "react";
import Button from "ui/Button/Button";
import TextField from "ui/TextField/TextField";
import type { Props as TextFieldProps } from "ui/TextField/TextField";
import SkeletonSearchProductPreview from "common/SearchProductPreview/SkeletonSearchProductPreview";
import { useStore } from "store";
import { gtm } from "helpers/analytics";
import { cn } from "helpers/style";
import styles from "./SearchSuggestions.module.scss";

type Props = TextFieldProps & {
  products: Array<ReactNode>;
  sts: Array<ReactNode>;
  taps: Array<ReactNode>;
  histories: Array<ReactNode>;
  goToSearchPage: (value?: string) => void;
  showSuggestions: boolean;
  success?: boolean;
  autoFocus?: boolean;
  isLoading: boolean;
  value: string;
  className?: string;
  classNameList?: string;
};

const SearchSuggestions = forwardRef<HTMLInputElement, Props>(
  (
    {
      products = [],
      sts = [],
      taps = [],
      histories = [],
      showSuggestions,
      value,
      onFocus,
      onChange,
      onKeyPress,
      isLoading,
      goToSearchPage,
      className,
      classNameList,
      ...props
    },
    ref
  ) => {
    const { header } = useStore();
    const notFound =
      value && !isLoading && !sts.length && !products.length && !taps.length;

    return (
      <div
        className={cn([styles.suggestions, className])}
        onWheel={e => e.stopPropagation()}
        onClick={gtm.autocompleteFieldClick}
      >
        <TextField
          className={cn([showSuggestions && styles.suggestionsTextFieldOpened])}
          classNameWrap={styles.suggestionsInput}
          classNamePrefix={styles.suggestionsInputPrefix}
          value={value}
          onFocus={onFocus}
          onChange={onChange}
          onKeyPress={onKeyPress}
          ref={ref}
          id="desktopSearchInput"
          autoComplete="off"
          {...props}
        />
        {showSuggestions && (
          <div className={cn([styles.suggestionsList, classNameList])}>
            {!isLoading && !!taps.length && (
              <div>
                <div className={styles.searchTaps}>
                  {taps.map((tap, index) => (
                    <li key={index} className={styles.searchTapsListItem}>
                      {tap}
                    </li>
                  ))}
                </div>
              </div>
            )}
            {!notFound && !!histories.length && (
              <div>
                <div className={styles.suggestionsTitle}>История</div>
                {histories.map((historyItem, index) => (
                  <li key={index}>{historyItem}</li>
                ))}
              </div>
            )}
            <div className={styles.suggestionsLeft}>
              {!isLoading && sts.length > 0 && (
                <>
                  <div className={styles.suggestionsTitle}>Часто ищут</div>
                  <ul className={styles.searchSts}>
                    {sts &&
                      sts
                        .slice(0, 3)
                        .map((item, index) => <li key={index}>{item}</li>)}
                  </ul>
                </>
              )}
            </div>
            <ul className={styles.suggestionsRight}>
              {!isLoading && products.length > 0 && (
                <>
                  <div className={styles.suggestionsTitle}>Товары</div>
                  {products &&
                    products
                      .slice(0, 3)
                      .map((product, index) => <li key={index}>{product}</li>)}
                </>
              )}
              {!isLoading && !!value && (
                <Button
                  orange
                  outlined
                  className={styles.suggestionsRightButton}
                  onClick={e => {
                    e && e.stopPropagation();
                    header.closeAllMenus();
                    gtm.autocompleteShowAllClick();
                    goToSearchPage(value);
                  }}
                >
                  Смотреть все товары
                </Button>
              )}
            </ul>
            {isLoading && (
              <ul className={styles.suggestionsRight}>
                {[0, 1, 2, 3].map(item => (
                  <li key={item}>
                    <SkeletonSearchProductPreview />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  }
);

SearchSuggestions.displayName = "SearchSuggestions";
export default SearchSuggestions;
